import { Alert, Button } from "@gotts.ai/ui";

import { TokenIcon } from "../../components/TokenIcon";
import { DEVENV_TOKEN_PRICES } from "../../lib/constants";
import { formatTokenDisplay, formatUsd } from "../../lib/format";

import { MAX_TICK, MIN_TICK } from "./types";

interface ConfirmationModalProps {
  protocol: "V2" | "V3" | "V4";
  tokenA: string;
  tokenB: string;
  amountA: string;
  amountB: string;
  /** V3/V4 specific */
  tickLower?: string;
  tickUpper?: string;
  tickLowerPrice?: string;
  tickUpperPrice?: string;
  feeTier?: string;
  poolShareEstimate?: string;
  poolTotalUsd?: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmationModal({
  protocol,
  tokenA,
  tokenB,
  amountA,
  amountB,
  tickLower,
  tickUpper,
  tickLowerPrice,
  tickUpperPrice,
  feeTier,
  poolShareEstimate,
  poolTotalUsd,
  onConfirm,
  onCancel,
}: ConfirmationModalProps) {
  const totalValueUsd =
    parseFloat(amountA || "0") * (DEVENV_TOKEN_PRICES[tokenA] ?? 0) +
    parseFloat(amountB || "0") * (DEVENV_TOKEN_PRICES[tokenB] ?? 0);

  const isFullRange =
    tickLower &&
    tickUpper &&
    parseInt(tickLower) <= MIN_TICK &&
    parseInt(tickUpper) >= MAX_TICK;

  // Determine number of transactions
  const txCount = protocol === "V2" ? 3 : protocol === "V3" ? 4 : 3;

  const protoPillClass =
    protocol === "V2"
      ? "protocol-pill-v2"
      : protocol === "V3"
        ? "protocol-pill-v3"
        : "protocol-pill-v4";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-testid="confirmation-modal"
    >
      {/* Backdrop */}
      <div
        className="animate-backdrop-in absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
      />
      {/* Modal */}
      <div className="animate-modal-in octa-md relative z-10 w-full max-w-sm bg-[rgba(16,18,22,0.95)] p-6 [backdrop-filter:var(--glass-blur)] [filter:drop-shadow(0_0_1px_rgba(201,168,76,0.35))_drop-shadow(0_8px_40px_rgba(0,0,0,0.5))]">
        <div className="section-tag">Confirm Add Liquidity</div>

        {/* Header row: protocol badge + token pair */}
        <div className="mb-4 flex items-center gap-2">
          <span className={`protocol-pill ${protoPillClass}`}>{protocol}</span>
          <div className="flex items-center gap-1.5">
            <TokenIcon symbol={tokenA} size={24} />
            <TokenIcon symbol={tokenB} size={24} />
            <span className="font-mono text-sm font-medium text-[var(--color-text)]">
              {tokenA}/{tokenB}
            </span>
          </div>
          {feeTier && (
            <span className={`protocol-pill ${protoPillClass} ml-auto`}>
              {(parseInt(feeTier) / 10000).toFixed(2)}%
            </span>
          )}
        </div>

        {/* Hero total value */}
        <div className="mb-4 text-center">
          <span className="hero-value" style={{ fontSize: 26 }}>
            {formatUsd(totalValueUsd)}
          </span>
        </div>

        <div className="space-y-3">
          {/* Amounts */}
          <div className="octa-sm bg-[rgba(255,255,255,0.04)] p-3">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5">
                <TokenIcon symbol={tokenA} size={20} />
                <span className="text-text-muted">{tokenA}</span>
              </span>
              <span className="text-text font-mono">
                {formatTokenDisplay(amountA).display || "0"}
                <span className="text-text-muted ml-1">
                  ({"\u2248"}{" "}
                  {formatUsd(
                    parseFloat(amountA || "0") *
                      (DEVENV_TOKEN_PRICES[tokenA] ?? 0),
                  )}
                  )
                </span>
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5">
                <TokenIcon symbol={tokenB} size={20} />
                <span className="text-text-muted">{tokenB}</span>
              </span>
              <span className="text-text font-mono">
                {formatTokenDisplay(amountB).display || "0"}
                <span className="text-text-muted ml-1">
                  ({"\u2248"}{" "}
                  {formatUsd(
                    parseFloat(amountB || "0") *
                      (DEVENV_TOKEN_PRICES[tokenB] ?? 0),
                  )}
                  )
                </span>
              </span>
            </div>
          </div>

          <div className="my-1" />

          {/* V2 fee tier */}
          {protocol === "V2" && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-text-muted">Fee Tier</span>
              <span className="text-text font-mono">0.30% (standard)</span>
            </div>
          )}

          {/* Pool share estimate */}
          {poolShareEstimate && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-text-muted">Est. Pool Share</span>
              <span className="text-text font-mono">{poolShareEstimate}</span>
            </div>
          )}

          {/* Large deposit warning */}
          {poolTotalUsd !== undefined &&
            (() => {
              const pct =
                poolTotalUsd > 0 ? (totalValueUsd / poolTotalUsd) * 100 : 0;
              return pct > 20 ? (
                <Alert variant="warning" title="Large Deposit">
                  This deposit is {pct.toFixed(0)}% of the current pool size.
                  Price impact may be significant.
                </Alert>
              ) : null;
            })()}

          {/* V3/V4 range details */}
          {(protocol === "V3" || protocol === "V4") &&
            tickLower &&
            tickUpper && (
              <div className="space-y-1 text-xs">
                <span className="text-text-muted">Price Range</span>
                {isFullRange ? (
                  <div className="mt-1">
                    <span className="badge-full-range">
                      Full Range (0 {"\u2192"} {"\u221e"})
                    </span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted pl-2">Min</span>
                      <span className="text-text ml-2 truncate font-mono">
                        {tickLowerPrice ?? tickLower}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted pl-2">Max</span>
                      <span className="text-text ml-2 truncate font-mono">
                        {tickUpperPrice ?? tickUpper}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

          {/* Transaction count note */}
          <p className="type-supporting text-center">
            This action will require{" "}
            <span className="font-semibold text-[var(--color-gold)]">
              {txCount}
            </span>{" "}
            blockchain transactions
          </p>
        </div>

        {/* Buttons */}
        <div className="mt-5 flex gap-3">
          <Button
            variant="outline"
            size="sm"
            className="basis-2/5"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant="gold"
            size="sm"
            className="basis-3/5"
            onClick={onConfirm}
          >
            {"\u2713"} Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}
